import type {
  AIDraftService,
  AIToolsConfigService,
} from '@affine/core/modules/ai-button';
import type { AIModelService } from '@affine/core/modules/ai-button/services/models';
import type {
  ServerService,
  SubscriptionService,
} from '@affine/core/modules/cloud';
import type { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import {
  CanvasElementType,
  EdgelessCRUDIdentifier,
} from '@blocksuite/affine/blocks/surface';
import { ConnectorMode } from '@blocksuite/affine/model';
import {
  DocModeProvider,
  NotificationProvider,
  TelemetryProvider,
} from '@blocksuite/affine/shared/services';
import type { EditorHost } from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  buildPortableCanvasTranscript,
  extractLatestChatTurn,
} from '../_common/chat-actions-handle';
import {
  type AIChatBlockModel,
  registerActiveAIChatBlockAutoSize,
} from '../blocks';
import type { SearchMenuConfig } from '../components/ai-chat-add-context';
import type { DocDisplayConfig } from '../components/ai-chat-chips';
import type {
  AINetworkSearchConfig,
  AIReasoningConfig,
} from '../components/ai-chat-input';
import type { ChatMessage } from '../components/ai-chat-messages';
import { ChatMessagesSchema } from '../components/ai-chat-messages';
import { AIProvider } from '../provider';
import { PeekViewStyles } from './styles';
import type { ChatContext } from './types';
import { calcChildBound } from './utils';

const createEmptyChatContext = (): ChatContext => ({
  status: 'idle',
  error: null,
  images: [],
  abortController: null,
  messages: [],
  databaseAttachments: [], // sl-57s
  automationAttachments: [],
  workflowAttachments: [], // sl-dpq2.5
  codeSelections: [],
  pdfAttachments: [], // sl-w252
  pdfContextAttachments: [], // sl-w252
});

export class AIChatBlockPeekView extends LitElement {
  static override styles = PeekViewStyles;

  private _conversationBlockModel: AIChatBlockModel | undefined;

  /**
   * One immutable source block for the complete send operation. The composer
   * stays mounted while users switch branches, so the cached conversation
   * block and the live canvas selection can differ. Capturing once keeps the
   * copied lineage, child placement, and connector on the same branch.
   */
  private _turnAnchorBlockModel: AIChatBlockModel | undefined;

  private get _currentBlockModel() {
    return this._conversationBlockModel ?? this.blockModel;
  }

  private _selectedConversationBlockModel() {
    const gfx = this.host.std.get(GfxControllerIdentifier);
    const selected = gfx.selection.selectedElements;
    if (
      selected.length === 1 &&
      'flavour' in selected[0] &&
      selected[0].flavour === 'affine:embed-ai-chat'
    ) {
      return selected[0] as AIChatBlockModel;
    }
    return this._currentBlockModel;
  }

  private get _modeService() {
    return this.host.std.get(DocModeProvider);
  }

  private get rootDocId() {
    return this._currentBlockModel.props.rootDocId || this.host.store.id;
  }

  private get rootWorkspaceId() {
    return (
      this._currentBlockModel.props.rootWorkspaceId ||
      this.host.store.workspace.id
    );
  }

  private _forkBlockId: string | undefined = undefined;

  private readonly _deserializeHistoryChatMessages = (
    historyMessagesString: string
  ) => {
    try {
      const result = ChatMessagesSchema.safeParse(
        JSON.parse(historyMessagesString)
      );
      if (result.success) {
        return result.data;
      } else {
        return [];
      }
    } catch {
      return [];
    }
  };

  private _lineageModels(
    startModel: AIChatBlockModel = this._currentBlockModel
  ): AIChatBlockModel[] {
    const lineage: AIChatBlockModel[] = [];
    const seen = new Set<string>();
    let current: AIChatBlockModel | undefined = startModel;
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      lineage.unshift(current);
      const parentId: string = current.props.parentBlockId;
      const parent = (
        parentId ? this.host.store.getBlock(parentId)?.model : undefined
      ) as AIChatBlockModel | undefined;
      current =
        parent?.flavour === 'affine:embed-ai-chat'
          ? (parent as AIChatBlockModel)
          : undefined;
    }
    return lineage;
  }

  private _lineageMessages(
    startModel: AIChatBlockModel = this._currentBlockModel
  ): ChatMessage[] {
    return this._lineageModels(startModel).flatMap(model =>
      this._deserializeHistoryChatMessages(model.props.messages)
    );
  }

  private _excludedCanvasSelectionBlockIds(): string[] {
    const ids = this._lineageModels().map(model => model.id);
    if (this._forkBlockId) ids.push(this._forkBlockId);
    return [...new Set(ids)];
  }

  private _sessionSnapshot(startModel: AIChatBlockModel) {
    return buildPortableCanvasTranscript(this._lineageMessages(startModel));
  }

  private readonly createForkSession = async () => {
    if (this.forkSession) {
      return this.forkSession;
    }
    const turnAnchor = this._selectedConversationBlockModel();
    this._turnAnchorBlockModel = turnAnchor;
    const response = await this.serverService.server.fetch(
      '/api/copilot/chat/shared-canvas-session',
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          workspaceId: this.rootWorkspaceId,
          docId: this.rootDocId,
          messages: this._sessionSnapshot(turnAnchor),
        }),
      }
    );
    if (!response.ok) {
      throw new Error(
        `Unable to create canvas chat session (${response.status})`
      );
    }
    const payload = (await response.json()) as { sessionId?: string };
    if (payload.sessionId) {
      const session = await AIProvider.session?.getSession(
        this.rootWorkspaceId,
        payload.sessionId
      );
      this.forkSession = session ?? null;
    }
    return this.forkSession;
  };

  private readonly _onChatStart = async () => {
    await this._createForkChatBlock('generating');
  };

  private readonly _onChatSuccess = async () => {
    const forkBlockId = await this._createForkChatBlock('finished');
    this.forkSession = null;
    if (forkBlockId) {
      await this._continueFromForkChatBlock(forkBlockId);
    }
  };

  private readonly _onChatError = async () => {
    await this._createForkChatBlock('error');
  };

  private readonly _onRequestClose = () => {
    this.peekViewService.peekView.close();
  };

  private readonly _prepareNextTurn = () => {
    this.chatContext = createEmptyChatContext();
    this._forkBlockId = undefined;
    this._turnAnchorBlockModel = undefined;
  };

  private readonly _continueFromForkChatBlock = async (forkBlockId: string) => {
    const forkBlock = this.host.store.getBlock(forkBlockId)?.model;
    if (forkBlock?.flavour !== 'affine:embed-ai-chat') {
      return;
    }
    this._conversationBlockModel = forkBlock as AIChatBlockModel;
    this._prepareNextTurn();
  };

  /**
   * Create a new AI chat block based on the current session and history messages
   */
  private readonly _createForkChatBlock = async (
    generationState: 'generating' | 'finished' | 'error'
  ) => {
    // Only create AI chat block in edgeless mode
    const mode = this._modeService.getEditorMode();
    if (mode !== 'edgeless') {
      return;
    }

    const messages = extractLatestChatTurn(this.chatContext.messages);
    if (!messages.length) {
      return;
    }

    // The in-flight placeholder and completed turn are the same block.
    if (this._forkBlockId) {
      const forkBlock = this.host.store.getBlock(this._forkBlockId)?.model;
      if (forkBlock?.flavour === 'affine:embed-ai-chat') {
        this.host.store.updateBlock(forkBlock, {
          messages: JSON.stringify(messages),
          generationState,
        });
        return this._forkBlockId;
      }
      // HMR, remote deletion, or an interrupted previous turn may leave a
      // stale id behind. Continue by creating a real child for this turn.
      this._forkBlockId = undefined;
    }

    // If there is no session id or chat messages, do not create a new chat block
    const forkSessionId = this.forkSession?.sessionId;
    if (!forkSessionId || !this.chatContext.messages.length) {
      return;
    }

    const { store } = this.host;
    // create a new AI chat block
    const surfaceBlock = store
      .getAllModels()
      .find(block => block.flavour === 'affine:surface');
    if (!surfaceBlock) {
      return;
    }

    const { rootWorkspaceId, rootDocId } = this;
    const currentUserId = (await AIProvider.userInfo)?.id ?? '';
    const canvasMode =
      this.aiToolsConfigService.config.value.agentMode === 'agent'
        ? 'agent'
        : 'chat';

    // The empty toolbar launcher is immutable too. Every completed turn gets
    // its own child, so simultaneous collaborators cannot overwrite one node.
    // Freeze one parent for the complete operation. Existing nodes never move;
    // only the new child and its connector are derived from this selection.
    const parentModel =
      this._turnAnchorBlockModel ?? this._selectedConversationBlockModel();
    const parentBlockId = parentModel.id;
    const bound = calcChildBound(parentModel, this.host.std);

    const crud = this.host.std.get(EdgelessCRUDIdentifier);
    const forkBlockId = crud.addBlock(
      'affine:embed-ai-chat',
      {
        xywh: bound.serialize(),
        messages: JSON.stringify(messages),
        sessionId: forkSessionId,
        rootWorkspaceId: rootWorkspaceId,
        rootDocId: rootDocId,
        parentBlockId,
        createdByUserId: currentUserId,
        canvasMode,
        goalCapability: 'disabled',
        generationState,
        schemaVersion: 3,
      },
      surfaceBlock.id
    );

    if (!forkBlockId) {
      return;
    }
    this._forkBlockId = forkBlockId;
    // The selected in-flight child is output, not request context. Refresh the
    // composer before selection propagation can render it as an "AI chat" pill.
    this.requestUpdate();
    registerActiveAIChatBlockAutoSize(forkBlockId);

    const gfx = this.host.std.get(GfxControllerIdentifier);
    const target = this.host.std.view.getBlock(forkBlockId);
    // Selection changes abort the original block's peek-view lifecycle. Move
    // its anchor first so that abort cannot close the still-active composer.
    this.peekViewService.peekView.retargetTarget(
      target ? { element: target } : {}
    );
    gfx.selection.set({ elements: [forkBlockId], editing: false });
    gfx.viewport.setViewport(
      gfx.viewport.zoom,
      [bound.x + bound.w / 2, bound.y + bound.h / 2],
      true
    );

    // Connect the parent chat block to the AI chat block
    crud.addElement(CanvasElementType.CONNECTOR, {
      mode: ConnectorMode.Curve,
      controllers: [],
      source: { id: parentBlockId, position: [1, 0.5] },
      target: { id: forkBlockId, position: [0, 0.5] },
    });

    const telemetryService = this.host.std.getOptional(TelemetryProvider);
    telemetryService?.track('CanvasElementAdded', {
      control: 'conversation',
      page: 'whiteboard editor',
      module: 'canvas',
      segment: 'whiteboard',
      type: 'chat block',
      category: 'branch',
    });

    return forkBlockId;
  };

  updateContext = (context: Partial<ChatContext>) => {
    this.chatContext = { ...this.chatContext, ...context };
  };

  override firstUpdated() {
    requestAnimationFrame(() => {
      const composer = this.renderRoot.querySelector('ai-chat-composer');
      const input = composer?.shadowRoot?.querySelector('ai-chat-input');
      input?.shadowRoot?.querySelector('textarea')?.focus();
    });
  }

  override render() {
    const { host, chatContext, updateContext, networkSearchConfig } = this;
    const notificationService = this.host.std.get(NotificationProvider);

    return html`<div class="ai-chat-block-peek-view-container">
      <ai-chat-composer
        .canvasAIChatContextMode=${'lineage-only'}
        .autoFocusInput=${true}
        .host=${host}
        .workspaceId=${this.rootWorkspaceId}
        .docId=${this.rootDocId}
        .session=${this.forkSession}
        .createSession=${this.createForkSession}
        .chatContextValue=${chatContext}
        .updateContext=${updateContext}
        .networkSearchConfig=${networkSearchConfig}
        .docDisplayConfig=${this.docDisplayConfig}
        .searchMenuConfig=${this.searchMenuConfig}
        .affineWorkspaceDialogService=${this.affineWorkspaceDialogService}
        .notificationService=${notificationService}
        .aiToolsConfigService=${this.aiToolsConfigService}
        .affineFeatureFlagService=${this.affineFeatureFlagService}
        .targetModeEnabled=${false}
        .excludedCanvasSelectionBlockIds=${this._excludedCanvasSelectionBlockIds()}
        .onRequestClose=${this._onRequestClose}
        .onChatStart=${this._onChatStart}
        .onChatSuccess=${this._onChatSuccess}
        .onChatError=${this._onChatError}
        .trackOptions=${{
          where: 'ai-chat-block',
          control: 'chat-send',
        }}
        .portalContainer=${this.parentElement}
        .reasoningConfig=${this.reasoningConfig}
        .serverService=${this.serverService}
        .subscriptionService=${this.subscriptionService}
        .aiModelService=${this.aiModelService}
        .onAISubscribe=${this.onAISubscribe}
      ></ai-chat-composer>
    </div> `;
  }

  @property({ attribute: false })
  accessor blockModel!: AIChatBlockModel;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor reasoningConfig!: AIReasoningConfig;

  @property({ attribute: false })
  accessor serverService!: ServerService;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  @property({ attribute: false })
  accessor affineWorkspaceDialogService!: WorkspaceDialogService;

  @property({ attribute: false })
  accessor aiDraftService!: AIDraftService;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor subscriptionService!: SubscriptionService;

  @property({ attribute: false })
  accessor onAISubscribe!: () => Promise<void>;

  @state()
  accessor chatContext: ChatContext = createEmptyChatContext();

  @state()
  accessor forkSession: CopilotChatHistoryFragment | null | undefined;
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-chat-block-peek-view': AIChatBlockPeekView;
  }
}

export const AIChatBlockPeekViewTemplate = (
  blockModel: AIChatBlockModel,
  host: EditorHost,
  docDisplayConfig: DocDisplayConfig,
  searchMenuConfig: SearchMenuConfig,
  networkSearchConfig: AINetworkSearchConfig,
  reasoningConfig: AIReasoningConfig,
  serverService: ServerService,
  affineFeatureFlagService: FeatureFlagService,
  peekViewService: PeekViewService,
  affineWorkspaceDialogService: WorkspaceDialogService,
  aiDraftService: AIDraftService,
  aiToolsConfigService: AIToolsConfigService,
  subscriptionService: SubscriptionService,
  aiModelService: AIModelService,
  onAISubscribe: (() => Promise<void>) | undefined
) => {
  return html`<ai-chat-block-peek-view
    .blockModel=${blockModel}
    .host=${host}
    .networkSearchConfig=${networkSearchConfig}
    .docDisplayConfig=${docDisplayConfig}
    .searchMenuConfig=${searchMenuConfig}
    .reasoningConfig=${reasoningConfig}
    .serverService=${serverService}
    .affineFeatureFlagService=${affineFeatureFlagService}
    .peekViewService=${peekViewService}
    .affineWorkspaceDialogService=${affineWorkspaceDialogService}
    .aiDraftService=${aiDraftService}
    .aiToolsConfigService=${aiToolsConfigService}
    .subscriptionService=${subscriptionService}
    .aiModelService=${aiModelService}
    .onAISubscribe=${onAISubscribe}
  ></ai-chat-block-peek-view>`;
};
